import type {
  Cadence,
  HeroKpiVisibility,
  ReportCustomerDto,
  ReportSectionDto,
} from '@act/shared/data-transfer-objects';
import {
  HERO_KPI_IDS,
  HERO_KPI_TOGGLE_LABELS,
  isReportSectionDisabled,
  isSectionIncludedInReport,
  normalizeHeroKpiVisibility,
  sectionSupportsInsightCards,
} from '@act/shared/data-transfer-objects';
import { FlexContainer } from '@act/storybook';
import { Typography } from '@mui/material';
import { getSectionMeta } from '../utils/reportDisplay.util';
import ReportDataRequirementTags from './ReportDataRequirementTags';
import ReportSectionPreview from './ReportSectionPreview';
import ReportSectionSettingToggle from './ReportSectionSettingToggle';
import ReportsSurface from './ReportsSurface';

export interface ReportBuilderSectionEditorProps {
  section: ReportSectionDto | undefined;
  customer: ReportCustomerDto | null | undefined;
  reportingPeriod: string;
  measurementPeriod: string;
  reportId: string;
  cadence: Cadence;
  savingSection: boolean;
  onIncludedChange: (section: ReportSectionDto, included: boolean) => void;
  onCalloutEnabledChange: (
    section: ReportSectionDto,
    calloutEnabled: boolean,
  ) => void;
  onClinicalInsightEnabledChange: (
    section: ReportSectionDto,
    enabled: boolean,
  ) => void;
  onFinancialInsightEnabledChange: (
    section: ReportSectionDto,
    enabled: boolean,
  ) => void;
  onHeroKpiVisibilityChange: (
    section: ReportSectionDto,
    patch: Partial<HeroKpiVisibility>,
  ) => void;
  onFieldBlur: (
    section: ReportSectionDto,
    field: 'narrative' | 'callout' | 'calloutTitle',
    value: string,
  ) => void;
  onInsightFieldChange: (
    section: ReportSectionDto,
    dataSnapshot: Record<string, unknown> | null,
    card: 'clinical' | 'financial',
    field: 'overline' | 'heading' | 'body',
    value: string,
  ) => void;
  onInsightFieldBlur: (
    section: ReportSectionDto,
    dataSnapshot: Record<string, unknown> | null,
    card: 'clinical' | 'financial',
    field: 'overline' | 'heading' | 'body',
    value: string,
  ) => void;
  onDataSnapshotChange: (
    section: ReportSectionDto,
    dataSnapshot: Record<string, unknown>,
  ) => void;
  onDataSnapshotBlur: (
    section: ReportSectionDto,
    dataSnapshot: Record<string, unknown>,
  ) => void;
}

/**
 * Main editor pane for the selected report section: settings, requirements, and preview.
 */
const ReportBuilderSectionEditor = ({
  section,
  customer,
  reportingPeriod,
  measurementPeriod,
  reportId,
  cadence,
  savingSection,
  onIncludedChange,
  onCalloutEnabledChange,
  onClinicalInsightEnabledChange,
  onFinancialInsightEnabledChange,
  onHeroKpiVisibilityChange,
  onFieldBlur,
  onInsightFieldChange,
  onInsightFieldBlur,
  onDataSnapshotChange,
  onDataSnapshotBlur,
}: ReportBuilderSectionEditorProps) => {
  const meta = section ? getSectionMeta(section.sectionId) : undefined;
  const insightCards =
    section !== undefined && sectionSupportsInsightCards(section.sectionId);

  if (!section || !meta) {
    return (
      <ReportsSurface padding={3}>
        <Typography color="text.secondary">
          Select a section to configure inclusion, insights, and preview content.
        </Typography>
      </ReportsSurface>
    );
  }

  const includeLocked = isReportSectionDisabled(section.sectionId);
  const includeEnabled = isSectionIncludedInReport(section);

  return (
    <FlexContainer
      direction="column"
      gap={2}
      flexGrow={1}
      minWidth="0"
      width="100%"
      height="auto"
      overflowAllVisible={true}
    >
      <ReportsSurface padding={3}>
        <FlexContainer
          direction="column"
          gap={2}
          width="100%"
          height="auto"
          overflowAllVisible={true}
        >
          <FlexContainer
            direction="column"
            gap={2}
            width="100%"
            height="auto"
            overflowAllVisible={true}
          >
            <FlexContainer
              direction="column"
              gap={0.5}
              width="100%"
              height="auto"
              overflowAllVisible={true}
            >
              <Typography variant="h5" component="h2">
                {meta.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {meta.rationale}
              </Typography>
              {meta.note ? (
                <Typography variant="body2" color="text.secondary">
                  {meta.note}
                </Typography>
              ) : null}
            </FlexContainer>
            <FlexContainer
              direction="row"
              gap={2}
              flexWrap={true}
              alignItems="center"
              width="100%"
              height="auto"
              overflowAllVisible={true}
            >
              <ReportSectionSettingToggle
                label="Include"
                value={includeEnabled}
                disabled={savingSection || includeLocked}
                onChange={included => onIncludedChange(section, included)}
              />
              {meta.supportsCallout ? (
                <ReportSectionSettingToggle
                  label="Callout"
                  value={section.calloutEnabled}
                  disabled={savingSection || !includeEnabled}
                  onChange={calloutEnabled =>
                    onCalloutEnabledChange(section, calloutEnabled)
                  }
                />
              ) : null}
              {section.sectionId === 'hero'
                ? HERO_KPI_IDS.map(kpiId => (
                    <ReportSectionSettingToggle
                      key={kpiId}
                      label={HERO_KPI_TOGGLE_LABELS[kpiId]}
                      value={
                        normalizeHeroKpiVisibility(section.heroKpiVisibility)[
                          kpiId
                        ]
                      }
                      disabled={savingSection || !includeEnabled}
                      onChange={enabled =>
                        onHeroKpiVisibilityChange(section, {
                          [kpiId]: enabled,
                        })
                      }
                    />
                  ))
                : null}
              {insightCards ? (
                <>
                  <ReportSectionSettingToggle
                    label="Insight #1"
                    value={section.clinicalInsightEnabled}
                    disabled={savingSection || !includeEnabled}
                    onChange={enabled =>
                      onClinicalInsightEnabledChange(section, enabled)
                    }
                  />
                  <ReportSectionSettingToggle
                    label="Insight #2"
                    value={section.financialInsightEnabled}
                    disabled={savingSection || !includeEnabled}
                    onChange={enabled =>
                      onFinancialInsightEnabledChange(section, enabled)
                    }
                  />
                </>
              ) : null}
            </FlexContainer>
          </FlexContainer>

          <ReportDataRequirementTags requirements={meta.dataRequirements} />

          <Typography variant="caption" color="text.secondary">
            Edit callout, insight, and Member Spotlight copy directly in the preview
            below.
          </Typography>
        </FlexContainer>
      </ReportsSurface>

      <FlexContainer
        direction="column"
        gap={1}
        width="100%"
        height="auto"
        overflowAllVisible={true}
      >
        <Typography
          variant="overline"
          color="text.secondary"
          style={{ letterSpacing: '0.08em' }}
        >
          Preview
        </Typography>
        <ReportSectionPreview
          section={{ ...section, source: 'draft' }}
          customer={customer}
          reportingPeriod={reportingPeriod}
          measurementPeriod={measurementPeriod}
          reportId={reportId}
          cadence={cadence}
          authoringEditable={true}
          onCalloutBlur={value => onFieldBlur(section, 'callout', value)}
          onCalloutTitleBlur={value =>
            onFieldBlur(section, 'calloutTitle', value)
          }
          onInsightFieldChange={(dataSnapshot, card, field, value) =>
            onInsightFieldChange(section, dataSnapshot, card, field, value)
          }
          onInsightFieldBlur={(dataSnapshot, card, field, value) =>
            onInsightFieldBlur(section, dataSnapshot, card, field, value)
          }
          onDataSnapshotChange={dataSnapshot =>
            onDataSnapshotChange(section, dataSnapshot)
          }
          onDataSnapshotBlur={dataSnapshot =>
            onDataSnapshotBlur(section, dataSnapshot)
          }
        />
      </FlexContainer>
    </FlexContainer>
  );
};

export default ReportBuilderSectionEditor;
